// Orders page › order detail: the card top-up a customer paid on the
// BargainFox Returns site for an exchange (Portal-BE
// GET /orders/:order_id/returns-payment). Shows what was paid, the sales
// invoice, every refund with its credit note, and, for staff with
// admin.orders.refund_issue, a Refund form: part or all of what is left, as
// many times as needed up to what the customer paid.
//
// Renders nothing for an order with no returns payment, and stays silent when
// the lookup is refused or fails (403, 404, 503 not_migrated): the panel is
// context, never a reason for the order modal to show an error.
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { CreditCard, FileText, ExternalLink, Mail } from "lucide-react";
import { useTranslation } from "../../i18n";
import {
    ReturnsPayment, ReturnsRefund, returnsPaymentForOrder, refundReturnsPayment, returnsPaymentDocumentPath,
    openProtectedFile, gbp, penceFrom,
} from "../../api/returns";
import "../../styles/admin-returns.css";

/** Checkout.com hadn't confirmed the refund yet: look again after this long. */
export const PENDING_RECHECK_MS = 5000;

const REASON_MIN = 3;
const REASON_MAX = 200;

const STATUS: Record<string, { key: string; pill: string }> = {
    refunded: { key: "rp_pay_status_refunded", pill: "green" },
    pending: { key: "rp_pay_status_waiting", pill: "amber" },
    requested: { key: "rp_pay_status_waiting", pill: "amber" },
    declined: { key: "rp_pay_status_declined", pill: "grey" },
    failed: { key: "rp_pay_status_failed", pill: "orange" },
};

const isUnsettled = (status: string | undefined) => status === "pending" || status === "requested";

/** 1234 → "12.34", the amount field's text. */
const poundsText = (pence: number) => gbp(pence).replace("£", "");

interface Formats { date: Intl.DateTimeFormat; dateTime: Intl.DateTimeFormat }
const when = (f: Intl.DateTimeFormat, iso: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : f.format(d);
};
const timeOf = (iso: string) => {
    const n = new Date(iso).getTime();
    return Number.isNaN(n) ? 0 : n;
};

const OrderReturnsPaymentPanel = ({ orderId, canIssueRefund }: { orderId: string; canIssueRefund: boolean }) => {
    const { t, language } = useTranslation();
    const [payments, setPayments] = useState<ReturnsPayment[] | null>(null);
    // The order the panel is showing; null once unmounted. A response for any
    // other order is dropped.
    const current = useRef<string | null>(orderId);
    const recheck = useRef<ReturnType<typeof setTimeout> | null>(null);
    const locale = language === "pl" ? "pl-PL" : "en-GB";
    const fmt = useMemo<Formats>(() => ({
        date: new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }),
        dateTime: new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }),
    }), [locale]);

    // The first answer decides whether the panel shows at all; a later refetch
    // that fails keeps what is already on screen.
    const load = useCallback(async () => {
        const forOrder = orderId;
        const r = await returnsPaymentForOrder(forOrder);
        if (current.current !== forOrder) return;
        if (r.ok && r.data && Array.isArray(r.data.payments)) setPayments(r.data.payments);
        else setPayments((prev) => prev ?? []);
    }, [orderId]);

    useEffect(() => {
        current.current = orderId;
        setPayments(null);
        void load();
        return () => {
            current.current = null;
            if (recheck.current) clearTimeout(recheck.current);
            recheck.current = null;
        };
    }, [orderId, load]);

    const afterRefund = useCallback((payment: ReturnsPayment | null, unsettled: boolean) => {
        if (payment && typeof payment.payment_row_id === "number") {
            setPayments((prev) => (prev ? prev.map((p) => (p.payment_row_id === payment.payment_row_id ? payment : p)) : prev));
        }
        void load();
        if (unsettled) {
            if (recheck.current) clearTimeout(recheck.current);
            recheck.current = setTimeout(() => {
                recheck.current = null;
                void load();
            }, PENDING_RECHECK_MS);
        }
    }, [load]);

    if (!payments || payments.length === 0) return null;

    return (
        <section className="rt-order-panel" aria-label={t("rp_pay_title")} data-testid="returns-payment-panel">
            <h3 className="rt-h3"><CreditCard size={14} /> {t("rp_pay_title")}</h3>
            {/* Not the refund for returned items (Jack, 2026-11-04: it was refunded by mistake for one). */}
            <p className="rt-muted rt-small rt-pay-hint" data-testid="returns-payment-hint">{t("rp_pay_hint")}</p>
            {payments.map((p) => (
                <PaymentBlock key={p.payment_row_id} orderId={orderId} payment={p} canIssueRefund={canIssueRefund} fmt={fmt} onRefund={afterRefund} />
            ))}
        </section>
    );
};

const PaymentBlock = ({ orderId, payment: p, canIssueRefund, fmt, onRefund }: {
    orderId: string;
    payment: ReturnsPayment;
    canIssueRefund: boolean;
    fmt: Formats;
    onRefund: (payment: ReturnsPayment | null, unsettled: boolean) => void;
}) => {
    const { t } = useTranslation();
    const [formOpen, setFormOpen] = useState(false);
    const refunds = useMemo(
        () => [...(Array.isArray(p.refunds) ? p.refunds : [])].sort((a, b) => timeOf(a.created_at) - timeOf(b.created_at)),
        [p.refunds],
    );

    const amount = gbp(p.amount_pence);
    const method = (p.paid_by || "").trim();
    const date = when(fmt.date, p.paid_at);
    const paidLine = method && date ? t("rp_pay_paid_full", { amount, method, date })
        : method ? t("rp_pay_paid_method", { amount, method })
        : date ? t("rp_pay_paid_date", { amount, date })
        : t("rp_pay_paid", { amount });

    // Nothing refunded or asked for yet: the paid line says it all.
    const totals: string[] = [];
    if (p.refunded_pence > 0) totals.push(t("rp_pay_refunded", { amount: gbp(p.refunded_pence) }));
    if (p.pending_pence > 0) totals.push(t("rp_pay_pending", { amount: gbp(p.pending_pence) }));
    if (totals.length > 0) {
        if (p.refundable_pence > 0) totals.push(t("rp_pay_left", { amount: gbp(p.refundable_pence) }));
        else if (p.pending_pence <= 0) totals.push(t("rp_pay_fully_refunded"));
    }

    const canRefund = canIssueRefund && p.can_refund && p.refundable_pence > 0;
    const blocked = canIssueRefund && !p.can_refund && !!p.refund_blocked_reason && p.refundable_pence > 0;
    const creditNotes = refunds.filter((r) => r.credit_note_number);

    const openDoc = async (number: string) => {
        const r = await openProtectedFile(returnsPaymentDocumentPath(orderId, number));
        if (r.notAvailable) toast.info(t("rt_not_available"));
        else if (!r.ok) toast.error(t("rt_file_failed"));
    };

    return (
        <div className="rp-pay" data-testid={`returns-payment-${p.payment_row_id}`}>
            <div className="rp-pay-head">
                <div className="rt-order-panel-main">
                    <span className="rt-order-panel-head">
                        <Link className="rt-ref-link rt-mono" to={`/admin/returns?tab=all&ref=${encodeURIComponent(p.return_reference)}`}>
                            {p.return_reference} <ExternalLink size={12} />
                        </Link>
                        <span className="rp-pay-paid">{paidLine}</span>
                    </span>
                    {totals.length > 0 && <span className="rt-muted rt-small" data-testid="returns-payment-totals">{totals.join(" · ")}</span>}
                    {blocked && <span className="rt-muted rt-small" data-testid="returns-payment-blocked">{p.refund_blocked_reason}</span>}
                </div>
                {canRefund && !formOpen && (
                    <button type="button" className="btn btn-sm rt-btn btn-secondary" onClick={() => setFormOpen(true)}>{t("rp_pay_refund")}</button>
                )}
            </div>

            {canRefund && formOpen && (
                <RefundForm
                    orderId={orderId}
                    payment={p}
                    onCancel={() => setFormOpen(false)}
                    onDone={(payment, unsettled) => { setFormOpen(false); onRefund(payment, unsettled); }}
                    onFailed={() => onRefund(null, false)}
                />
            )}

            {(p.invoice || creditNotes.length > 0) && (
                <div className="rt-order-panel-invoices">
                    {p.invoice && (
                        <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={() => openDoc(p.invoice!.number)}>
                            <FileText size={14} /> {t("rp_pay_invoice", { number: p.invoice.number })}
                        </button>
                    )}
                    {creditNotes.map((r) => (
                        <button key={r.id} type="button" className="btn btn-sm rt-btn btn-ghost" onClick={() => openDoc(r.credit_note_number!)}>
                            <FileText size={14} /> {t("rp_pay_credit_note", { number: r.credit_note_number!, amount: gbp(r.amount_pence) })}
                        </button>
                    ))}
                </div>
            )}

            {refunds.length > 0 && (
                <>
                    <h4 className="rt-h3 rp-pay-h4">{t("rp_pay_history")}</h4>
                    <ul className="rp-pay-refunds">
                        {refunds.map((r) => <RefundRow key={r.id} refund={r} fmt={fmt} onOpen={openDoc} />)}
                    </ul>
                </>
            )}
        </div>
    );
};

const RefundRow = ({ refund: r, fmt, onOpen }: { refund: ReturnsRefund; fmt: Formats; onOpen: (number: string) => void }) => {
    const { t } = useTranslation();
    const status = STATUS[r.status];
    // No staff name (a refund from the dashboard, a cancelled exchange, …): say where it came from.
    const sourceKey = `rp_pay_source_${r.source}`;
    const sourceLabel = r.source ? t(sourceKey) : "";
    const who = r.by_name || (sourceLabel === sourceKey ? r.source : sourceLabel);
    return (
        <li className="rp-pay-refund">
            <span className="rp-pay-refund-main">
                <span className="rp-pay-refund-line">
                    <span className="rt-muted">{when(fmt.dateTime, r.created_at)}</span>
                    <span className="rt-strong">{gbp(r.amount_pence)}</span>
                    <span className={`rt-pill rt-pill--${status?.pill ?? "grey"}`}>{status ? t(status.key) : r.status}</span>
                </span>
                {(r.reason || who) && (
                    <span>
                        {r.reason}
                        {who && <span className="rt-muted">{r.reason ? " · " : ""}{who}</span>}
                    </span>
                )}
                {r.error && <span className="rt-error rt-small">{r.error}</span>}
                {r.customer_emailed && <span className="rt-muted rt-small rp-pay-emailed"><Mail size={12} /> {t("rp_pay_emailed")}</span>}
            </span>
            {r.credit_note_number && (
                <button type="button" className="btn btn-sm rt-btn btn-ghost" aria-label={t("rp_pay_open_credit_note", { number: r.credit_note_number })}
                    onClick={() => onOpen(r.credit_note_number!)}>
                    <FileText size={14} /> {r.credit_note_number}
                </button>
            )}
        </li>
    );
};

const RefundForm = ({ orderId, payment, onCancel, onDone, onFailed }: {
    orderId: string;
    payment: ReturnsPayment;
    onCancel: () => void;
    onDone: (payment: ReturnsPayment | null, unsettled: boolean) => void;
    onFailed: () => void;
}) => {
    const { t } = useTranslation();
    const max = payment.refundable_pence;
    const [amountText, setAmountText] = useState(() => poundsText(max));
    const [reason, setReason] = useState("");
    const [reasonTouched, setReasonTouched] = useState(false);
    const [notify, setNotify] = useState(true);
    // Staff confirm it's the exchange payment they mean to give back, not a return's refund.
    const [understood, setUnderstood] = useState(false);
    const [sending, setSending] = useState(false);
    // Money moves on this click: a second click before the first answers must not send twice.
    const inFlight = useRef(false);
    const id = `rp-pay-${payment.payment_row_id}`;

    // Accept "5,00" too (a Polish keyboard's decimal mark).
    const pence = penceFrom(amountText.replace(",", "."));
    const amountError = pence === null ? t("rp_pay_amount_invalid")
        : pence < 1 ? t("rp_pay_amount_min")
        : pence > max ? t("rp_pay_amount_max", { amount: gbp(max) })
        : null;
    const reasonText = reason.trim();
    const reasonError = reasonText.length < REASON_MIN ? t("rp_pay_reason_short") : null;
    const valid = !amountError && !reasonError && pence !== null && understood;
    const method = (payment.paid_by || "").trim();
    const confirmText = pence !== null && !amountError
        ? (method ? t("rp_pay_confirm", { amount: gbp(pence), method }) : t("rp_pay_confirm_plain", { amount: gbp(pence) }))
        : t("rp_pay_refund");

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setReasonTouched(true);
        if (!valid || pence === null || inFlight.current) return;
        inFlight.current = true;
        setSending(true);
        try {
            const r = await refundReturnsPayment(orderId, {
                payment_row_id: payment.payment_row_id,
                amount_pence: pence,
                reason: reasonText,
                notify_customer: notify,
            });
            if (r.ok && r.data) {
                const refund = r.data.refund;
                const shown = gbp(refund && typeof refund.amount_pence === "number" ? refund.amount_pence : pence);
                if (refund?.status === "refunded") toast.success(t("rp_pay_done", { amount: shown }));
                else toast.info(t("rp_pay_sent", { amount: shown }));
                onDone(r.data.payment ?? null, !refund || isUnsettled(refund.status));
            } else {
                toast.error(r.message || t("rp_pay_failed"));
                // What is left to refund may have moved (409), or a lost answer
                // may still have gone through: show the server's view.
                onFailed();
            }
        } finally {
            inFlight.current = false;
            setSending(false);
        }
    };

    return (
        <form className="rp-pay-form" onSubmit={submit} aria-label={t("rp_pay_refund")} noValidate>
            <label className="rt-label" htmlFor={`${id}-amount`}>{t("rp_pay_amount")}</label>
            <div className="rp-pay-amount">
                <input
                    id={`${id}-amount`}
                    className={`rt-input${amountError ? " rt-input--error" : ""}`}
                    inputMode="decimal"
                    autoComplete="off"
                    value={amountText}
                    disabled={sending}
                    aria-invalid={!!amountError}
                    aria-describedby={amountError ? `${id}-amount-error` : undefined}
                    onChange={(e) => setAmountText(e.target.value)}
                />
                <button type="button" className="btn btn-sm rt-btn btn-ghost" disabled={sending} onClick={() => setAmountText(poundsText(max))}>
                    {t("rp_pay_full_amount")}
                </button>
                <span className="rt-muted rt-small">{t("rp_pay_up_to", { amount: gbp(max) })}</span>
            </div>
            {amountError && <p className="rt-field-error" id={`${id}-amount-error`}>{amountError}</p>}

            <label className="rt-label" htmlFor={`${id}-reason`}>{t("rp_pay_reason")}</label>
            <input
                id={`${id}-reason`}
                className={`rt-input${reasonTouched && reasonError ? " rt-input--error" : ""}`}
                maxLength={REASON_MAX}
                autoComplete="off"
                placeholder={t("rp_pay_reason_placeholder")}
                value={reason}
                disabled={sending}
                aria-invalid={reasonTouched && !!reasonError}
                aria-describedby={reasonTouched && reasonError ? `${id}-reason-error` : undefined}
                onChange={(e) => setReason(e.target.value)}
                onBlur={() => setReasonTouched(true)}
            />
            {reasonTouched && reasonError && <p className="rt-field-error" id={`${id}-reason-error`}>{reasonError}</p>}

            <label className="rs-check rp-pay-notify">
                <input type="checkbox" checked={notify} disabled={sending} onChange={(e) => setNotify(e.target.checked)} />
                <span>{t("rp_pay_notify")}</span>
            </label>
            <label className="rs-check rp-pay-notify" data-testid="returns-payment-understand">
                <input type="checkbox" checked={understood} disabled={sending} onChange={(e) => setUnderstood(e.target.checked)} />
                <span><strong>{t("rp_pay_understand")}</strong></span>
            </label>

            <div className="rp-pay-form-foot">
                <button type="submit" className="btn btn-sm rt-btn btn-primary" disabled={!valid || sending}>
                    {sending ? t("rp_pay_sending") : confirmText}
                </button>
                <button type="button" className="btn btn-sm rt-btn btn-ghost" disabled={sending} onClick={onCancel}>{t("cancel")}</button>
            </div>
        </form>
    );
};

export default OrderReturnsPaymentPanel;
